/**
 * Tenant/Organization selector component
 * Allows users to switch between organizations they have access to
 */
import { useState, useEffect } from 'react'
import { useAuth } from '@/global/auth/AuthContext'
import { supabase } from '@/lib/supabase'
import { Button } from '@/global/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/global/components/ui/dropdown-menu'
import { ChevronDown, Building2, Check } from 'lucide-react'

interface Organization {
  id: string
  name: string
  type: 'ORG' | 'TEAM' // Corrected to match schema
  fma_plan: string | null
}

export function TenantSelector() {
  const { user, activeTenantId, selectTenant } = useAuth()
  const [organizations, setOrganizations] = useState<Organization[]>([])
  const [loading, setLoading] = useState(true)
  const [switching, setSwitching] = useState(false)

  useEffect(() => {
    async function loadOrganizations() {
      if (!user) {
        setLoading(false)
        return
      }

      try {
        // Get all organizations the user is a member of
        const { data, error } = await supabase
          .from('memberships') // Corrected table name
          .select(`
            organization_id,
            organizations!inner (
              id,
              name,
              type
            )
          `)
          .eq('user_id', user.id)
          .eq('disabled', false) // Corrected column name

        if (error) throw error

        const orgList = data?.map((membership: any) => ({
          id: membership.organization_id, // Corrected field name
          name: membership.organizations.name, // Corrected relationship name
          type: membership.organizations.type,
          fma_plan: null // Will get from function if needed
        })) || []

        setOrganizations(orgList)
      } catch (error) {
        console.error('Failed to load organizations:', error)
      } finally {
        setLoading(false)
      }
    }

    loadOrganizations()
  }, [user])

  const handleTenantSwitch = async (orgId: string) => {
    if (orgId === activeTenantId || switching) return

    setSwitching(true)
    try {
      await selectTenant(orgId)
      // Reload the page to refresh app state with new tenant
      window.location.reload()
    } catch (error) {
      console.error('Failed to switch tenant:', error)
      setSwitching(false)
    }
  }

  const activeOrg = organizations.find(org => org.id === activeTenantId)

  if (loading) {
    return (
      <div className="flex items-center space-x-2">
        <Building2 className="h-4 w-4" />
        <span className="text-sm">Loading...</span>
      </div>
    )
  }

  if (organizations.length === 0) {
    return (
      <div className="flex items-center space-x-2 text-muted-foreground">
        <Building2 className="h-4 w-4" />
        <span className="text-sm">No organizations</span>
      </div>
    )
  }

  if (organizations.length === 1) {
    return (
      <div className="flex items-center space-x-2">
        <Building2 className="h-4 w-4" />
        <span className="text-sm font-medium">{activeOrg?.name || 'Unknown'}</span>
        {activeOrg?.fma_plan && (
          <span className="text-xs bg-secondary px-2 py-1 rounded capitalize">
            {activeOrg.fma_plan.replace('fma_', '')}
          </span>
        )}
      </div>
    )
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="ghost" 
          className="flex items-center space-x-2 h-auto p-2"
          disabled={switching}
        >
          <Building2 className="h-4 w-4" />
          <div className="flex flex-col items-start">
            <span className="text-sm font-medium">
              {activeOrg?.name || 'Select Organization'}
            </span>
            {activeOrg?.fma_plan && (
              <span className="text-xs text-muted-foreground capitalize">
                {activeOrg.fma_plan.replace('fma_', '')} plan
              </span>
            )}
          </div>
          <ChevronDown className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      
      <DropdownMenuContent align="start" className="w-64">
        <DropdownMenuLabel>Switch Organization</DropdownMenuLabel>
        <DropdownMenuSeparator />
        
        {organizations.map((org) => (
          <DropdownMenuItem
            key={org.id}
            onClick={() => handleTenantSwitch(org.id)}
            className="flex items-center justify-between cursor-pointer"
          >
            <div className="flex flex-col">
              <span className="font-medium">{org.name}</span>
              <div className="flex items-center space-x-2 text-xs text-muted-foreground">
                <span className="capitalize">{org.type}</span>
                {org.fma_plan && (
                  <>
                    <span>•</span>
                    <span className="capitalize">{org.fma_plan.replace('fma_', '')} plan</span>
                  </>
                )}
              </div>
            </div>
            {org.id === activeTenantId && (
              <Check className="h-4 w-4 text-primary" />
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

// Simplified version for navigation bars
export function TenantSelectorCompact() {
  const { activeTenantId } = useAuth()
  const [activeOrg, setActiveOrg] = useState<Organization | null>(null)

  useEffect(() => {
    async function loadActiveOrg() {
      if (!activeTenantId) return

      try {
        const { data, error } = await supabase
          .from('organizations') // Corrected table name
          .select('id, name, type')
          .eq('id', activeTenantId)
          .single()

        if (error) throw error
        setActiveOrg({ ...data, fma_plan: null }) // Add fma_plan as null
      } catch (error) {
        console.error('Failed to load active org:', error)
      }
    }

    loadActiveOrg()
  }, [activeTenantId])

  if (!activeOrg) {
    return null
  }

  return (
    <div className="flex items-center space-x-2 text-sm">
      <Building2 className="h-4 w-4 text-muted-foreground" />
      <span className="font-medium">{activeOrg.name}</span>
      {activeOrg.fma_plan && (
        <span className="text-xs bg-secondary px-2 py-1 rounded capitalize">
          {activeOrg.fma_plan.replace('fma_', '')}
        </span>
      )}
    </div>
  )
}
